import { useState, useContext, useEffect } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { ThemeContext } from '../Context';
import Elements from './Elements';
import Rubrics from './Rubrics';
import StandardReports from './StandardReports';
import Appendix from './Appendix';
// Partie gauche (left) de l'application, elle propose la liste des rubriques à modifier.
function Left() {
  const [activeView, setActiveView] = useState('elements'); // 'elements' par défaut
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const { theme } = useContext(ThemeContext);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Observer pour détecter quand TinyMCE entre en mode fullscreen
  useEffect(() => {
    const checkFullscreen = () => {
      setIsEditorFullscreen(document.body.classList.contains('tox-fullscreen'));
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkFullscreen();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    checkFullscreen();

    return () => observer.disconnect();
  }, []);

  // Ne pas afficher quand TinyMCE est en mode fullscreen
  if (isEditorFullscreen) {
    return null;
  }

  const handleViewChange = (view) => {
    setActiveView(view);
  };

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Barre de boutons fixée en haut */}
      <Box
        sx={{
          flexShrink: 0, // Empêche la barre de boutons de rétrécir
          zIndex: 10
        }}
      >
        <Stack 
          direction="row" 
          spacing={0} 
          sx={{ 
            background: isDark 
              ? 'rgb(40, 57, 73)' 
              : 'rgb(218, 230, 236)',
            boxShadow: isDark 
              ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
              : '0 2px 8px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
          }}
        >
          <Button
            onClick={() => handleViewChange('elements')}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: activeView === 'elements' ? 600 : 500,
              py: 1,
              fontSize: '0.875rem',
              color: activeView === 'elements' 
                ? 'primary.main' 
                : (isDark ? '#bdc3c7' : '#666'),
              backgroundColor: 'transparent',
              borderRadius: 0,
              position: 'relative',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : '#f5f5f5',
                color: 'primary.main',
              },
              transition: 'all 0.2s ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: activeView === 'elements' ? '60%' : '0',
                height: '2px',
                backgroundColor: 'primary.main',
                transition: 'width 0.3s ease',
              },
            }}
          >
            Encodage
          </Button>

          <Button
            onClick={() => handleViewChange('rubrics')}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: activeView === 'rubrics' ? 600 : 500,
              py: 1,
              fontSize: '0.875rem',
              color: activeView === 'rubrics' 
                ? 'primary.main' 
                : (isDark ? '#bdc3c7' : '#666'),
              backgroundColor: 'transparent',
              borderRadius: 0,
              position: 'relative',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : '#f5f5f5',
                color: 'primary.main',
              },
              transition: 'all 0.2s ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: activeView === 'rubrics' ? '60%' : '0',
                height: '2px',
                backgroundColor: 'primary.main',
                transition: 'width 0.3s ease',
              },
            }}
          >
            Rubriques
          </Button>

          <Button
            onClick={() => handleViewChange('standardReports')}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: activeView === 'standardReports' ? 600 : 500,
              py: 1,
              fontSize: '0.875rem',
              color: activeView === 'standardReports' 
                ? 'primary.main' 
                : (isDark ? '#bdc3c7' : '#666'),
              backgroundColor: 'transparent',
              borderRadius: 0,
              position: 'relative',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : '#f5f5f5',
                color: 'primary.main',
              },
              transition: 'all 0.2s ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: activeView === 'standardReports' ? '60%' : '0',
                height: '2px',
                backgroundColor: 'primary.main',
                transition: 'width 0.3s ease',
              },
            }}
          >
            Canevas
          </Button>

          <Button
            onClick={() => handleViewChange('appendix')}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: activeView === 'appendix' ? 600 : 500,
              py: 1,
              fontSize: '0.875rem',
              color: activeView === 'appendix' 
                ? 'primary.main' 
                : (isDark ? '#bdc3c7' : '#666'),
              backgroundColor: 'transparent',
              borderRadius: 0,
              position: 'relative',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : '#f5f5f5',
                color: 'primary.main',
              },
              transition: 'all 0.2s ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: activeView === 'appendix' ? '60%' : '0',
                height: '2px',
                backgroundColor: 'primary.main',
                transition: 'width 0.3s ease',
              },
            }}
          >
            Annexes
          </Button>
        </Stack>
      </Box>

      {/* Zone de contenu défilable */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          backgroundColor: isDark ? '#1a252f' : 'inherit'
        }}
      >
        {/* Tous les composants sont toujours montés pour préserver les états, mais masqués visuellement */}
        <Box sx={{ display: activeView === 'elements' ? 'block' : 'none', height: '100%' }}>
          <Elements />
        </Box>
        <Box sx={{ display: activeView === 'rubrics' ? 'block' : 'none', height: '100%' }}>
          <Rubrics />
        </Box>
        <Box sx={{ display: activeView === 'standardReports' ? 'block' : 'none', height: '100%' }}>
          <StandardReports />
        </Box>
        <Box sx={{ display: activeView === 'appendix' ? 'block' : 'none', height: '100%' }}>
          <Appendix />
        </Box>
      </Box>
    </Box>
  );
}

export default Left;



